import DebugPrinter from "components/Debug/DebugPrinter";
import SizeSelector from 'components/Infographics/Filters/SizeSelector';
import dayjs from 'dayjs';
import useFetchDataWithOrg from "hooks/useFetchDataWithOrg";
import useFetchEntity from "hooks/useFetchEntity";
import usePhaseIdTranslator from 'hooks/usePhaseIdTranslator';
import { useMemo, useRef } from "react";
import usePersistentStore from 'store/persistentStore';
import BackgroundSelector from "./Filters/BackgroundSelector";
import IgImgPlacement from "./Filters/IgImgPlacement";
import IgImgSelector from "./Filters/IgImgSelector";
import MarginEditor from "./Filters/MarginEditor";
import PhaseSelector from "./Filters/PhaseSelector";
import useZoomByMouseWheel from "./Hooks/useZoomByMouseWheel";
import { createForegroundPositionExpression, createHandleFgImgMouseMove, dateFormatIG } from "./IGconfig";
import CompetitionLogo from "./Parts/CompetitionLogo";
import DotRotatedText from "./Parts/DotRotatedText";
import IGHeader from "./Parts/IGHeader";
import TablePlayers from "./Parts/TablePlayers";

const IG14_StarLeaders = ({ showData, exportRef }) => {
  const membership = usePersistentStore((state) => state.get('ig-membership'));
  const setMembership = usePersistentStore((state) => state.set('ig-membership'));
  const fgPictureUrl = usePersistentStore((state) => state.get('ig-starleaders-fg-url'));
  const setFgPictureUrl = usePersistentStore((state) => state.set('ig-starleaders-fg-url'));
  const size = usePersistentStore((state) => state.get('ig-starleaders-size'));
  const setSize = usePersistentStore((state) => state.set('ig-starleaders-size'));
  const SIZE_PRESETS = ['PORTRAIT', 'CUSTOM'];
  const DEFAULT_SIZE = { width: 625, height: 1111, preset: 'PORTRAIT' };

  const fgImgPositionX = usePersistentStore((state) => state.get('ig-starleaders-fgimgposition-x')) ?? 0;
  const setFgImgPositionX = usePersistentStore((state) => state.set('ig-starleaders-fgimgposition-x'));
  const fgImgPositionY = usePersistentStore((state) => state.get('ig-starleaders-fgimgposition-y')) ?? 0;
  const setFgImgPositionY = usePersistentStore((state) => state.set('ig-starleaders-fgimgposition-y'));
  const fgImgZoom = usePersistentStore((state) => state.get('ig-starleaders-fgzoom')) ?? 100;
  const setFgImgZoom = usePersistentStore((state) => state.set('ig-starleaders-fgzoom'));
  const fgImgRef = useRef(null);

  const igname = 'starleaders';
  const marginDefaults = { top: 0, left: 0, bottom: 0, right: 0 };
  const fgImgMargin = usePersistentStore((state) => state.get(`ig-${igname}-margin`)) ?? marginDefaults;
  const setFgImgMargin = usePersistentStore((state) => state.set(`ig-${igname}-margin`));

  useZoomByMouseWheel(fgImgRef, fgImgZoom, setFgImgZoom);

  // Background image and color
  const backgroundImgUrl = usePersistentStore((state) => state.get('ig-starleaders-background-img-url'));
  const setBackgroundImgUrl = usePersistentStore((state) => state.set('ig-starleaders-background-img-url'));
  const backgroundColor = usePersistentStore((state) => state.get('ig-starleaders-background-img-color'));
  const setBackgroundColor = usePersistentStore((state) => state.set('ig-starleaders-background-img-color'));
  const backgroundMode = usePersistentStore((state) => state.get('ig-starleaders-background-mode'))
    ?? (backgroundImgUrl ? 'IMAGE' : backgroundColor ? 'COLOR' : 'DEFAULT');
  const setBackgroundMode = usePersistentStore((state) => state.set('ig-starleaders-background-mode'));
  const handleFgImgMouseMove = createHandleFgImgMouseMove(setFgImgPositionX, setFgImgPositionY);


  const groupId = membership?.groupId;
  const seasonId = membership?.seasonId;
  const { phaseIdsAll } = usePhaseIdTranslator(membership);

  const { data: groupDetail } = useFetchEntity('Group', groupId, 'Season>Competition');
  const { data: seasonDetail } = useFetchEntity('Season', seasonId, 'Competition');

  const playerStats = useFetchDataWithOrg(phaseIdsAll
    ? `/admin/tablePlayersStats?phaseIds=${phaseIdsAll.join(',')}`
    : null);

  const metadata = useMemo(() => {
    return {
      GroupName: groupDetail?.name,
      GroupColor: groupDetail?.color,
      SeasonName: seasonDetail?.name,
      CompetitionName: seasonDetail?.Competition?.name,
      CompetitionLogo:
        seasonDetail?.Competition?.logoUrl?.replace(
          "[size]",
          "cropped_md",
        ),
      Date: dayjs().format(dateFormatIG),
    };
  }, [groupDetail, seasonDetail]);

  // prepare data


  const data = useMemo(() => {
    const data = playerStats?.data?.map((player) => ({
      FirstName: player.FirstName,
      LastName: player.LastName,
      PlayerLogo: player.PlayerLogo?.replace("[size]", "cropped_md"),
      PlayerLevelCode: player.PlayerLevelCode,
      TeamLogo: player.TeamLogo?.replace("[size]", "cropped_md"),
      Z: player.GP,
      G: player.G,
      A: player.A,
      P: player.P,
      TM: player.PIM,
      Stars: player.Stars,
    })) ?? [];

    const sorted = data.sort((a, b) => {
      return b.Stars - a.Stars;
    });

    return sorted.slice(0, 15);
  }, [playerStats]);

  return (
    <>
      <div className="grid grid-cols-[auto,1fr] gap-3 items-center">

        <span>Membership:</span>
        <PhaseSelector
          value={membership}
          onChange={setMembership}
          mandatory={['competitionId', 'seasonId']}
        />

        <span>Foreground image:</span>
        <div>
          <IgImgSelector fgPictureUrl={fgPictureUrl} setFgPictureUrl={setFgPictureUrl} igId={14} />
        </div>

        <span className="relative top-[-14px]">Foreground placement:</span>
        <div >
          <IgImgPlacement
            fgImgPositionX={fgImgPositionX}
            fgImgPositionY={fgImgPositionY}
            setFgImgPositionX={setFgImgPositionX}
            setFgImgPositionY={setFgImgPositionY}
            fgImgZoom={fgImgZoom}
            setFgImgZoom={setFgImgZoom}
          />
        </div>

        <span>Background:</span>
        <BackgroundSelector
          backgroundMode={backgroundMode}
          setBackgroundMode={setBackgroundMode}
          backgroundImgUrl={backgroundImgUrl}
          setBackgroundImgUrl={setBackgroundImgUrl}
          backgroundColor={backgroundColor}
          setBackgroundColor={setBackgroundColor}
          igId={14}
        />

        <span>Dimensions:</span>
        <div className="flex gap-2 items-center">
          <SizeSelector
            value={size}
            onChange={setSize}
            presets={SIZE_PRESETS}
            defaultValue={DEFAULT_SIZE}
          />
        </div>

        <span>Margin:</span>
        <div className="flex gap-2 items-center">
          <MarginEditor
            margin={fgImgMargin}
            setMargin={setFgImgMargin}
            defaults={marginDefaults}
          />
        </div>

        <div />
      </div>

      {showData && (<DebugPrinter data={[groupDetail, playerStats, data, metadata]}></DebugPrinter>)}
      {playerStats && phaseIdsAll &&
        <article
          ref={exportRef}
          className="mx-auto w-[625px] bg-hockey-page-bg font-header text-white"
          style={{
            width: `${size?.width}px`,
            height: `${size?.height}px`,
            paddingTop: `${fgImgMargin.top}px`,
            paddingLeft: `${fgImgMargin.left}px`,
            paddingRight: `${fgImgMargin.right}px`,
            paddingBottom: `${fgImgMargin.bottom}px`,
            ...(backgroundMode === 'COLOR' && backgroundColor
              ? { backgroundColor }
              : backgroundMode === 'IMAGE' && backgroundImgUrl
                ? {
                    backgroundImage: `url('${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${backgroundImgUrl}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }
                : {
                    backgroundColor: '#323f50',
                  }),
          }}
        >
          <div className="flex h-full flex-col items-center justify-between">

            <div className="p-8 pb-6">
              <IGHeader
                title={`League leaders`}
                league={metadata.GroupName}
                leagueStyle={{ color: metadata.GroupColor ?? '#777777' }}
                small
                date={metadata.Date}
              />
              <TablePlayers data={data} highlightColID="stars" />
            </div>
            <div className='w-full'>

              <DotRotatedText firstText="Stars" secondText="Leaders" />

              <div
                ref={fgImgRef}
                onMouseMove={handleFgImgMouseMove}
                className='w-full z-20 relative aspect-[24/9]'
                style={{
                  backgroundImage: `url('${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${fgPictureUrl}')`,
                  backgroundSize: `${fgImgZoom ?? 100}%`,
                  backgroundPosition: createForegroundPositionExpression(fgImgPositionX, fgImgPositionY),
                  backgroundRepeat: 'no-repeat',
                  cursor: 'grab', // Visual cue for draggable behavior
                  clipPath: "polygon(450px 0, 100% 0, 100% 100%, 0 100%, 0 70px)",
                }}
                onMouseDown={(e) => e.target.style.cursor = 'grabbing'} // Change cursor while dragging
                onMouseUp={(e) => e.target.style.cursor = 'grab'} // Reset cursor
              ></div>
              <CompetitionLogo
                className="mx-auto scale-75 py-2"
                src={metadata.CompetitionLogo}
                title={metadata.CompetitionName}
                season={metadata.SeasonName}
              />
            </div>
          </div>
        </article>
      }
    </>
  );
};

export default IG14_StarLeaders;
